import { FC, useMemo } from 'react';
import { X, FileText } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { useLanguage } from '../../contexts/LanguageContext';

interface NotesDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  notes: string;
  title?: string;
}

export const NotesDetailModal: FC<NotesDetailModalProps> = ({
  isOpen,
  onClose,
  notes,
  title
}) => {
  const { t } = useLanguage();

  // Prevent body scroll when modal is open
  if (isOpen) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = 'unset';
  }

  if (!isOpen) return null;

  // Determine if content contains HTML tags (simple detection)
  const hasHTML = useMemo(() => {
    if (!notes) return false;
    const htmlTagPattern = /<[a-z][\s\S]*>/i;
    return htmlTagPattern.test(notes);
  }, [notes]);

  // Sanitize content - use different settings for HTML vs Markdown
  const sanitizedContent = useMemo(() => {
    if (!notes) return '';

    if (hasHTML) {
      // For HTML content, allow more tags but sanitize dangerous attributes
      return DOMPurify.sanitize(notes, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre', 'blockquote', 'a', 'span', 'div', 'hr'],
        ALLOWED_ATTR: ['href', 'title', 'class', 'target'],
        ALLOW_DATA_ATTR: false,
      });
    } else {
      // For Markdown, let ReactMarkdown handle parsing, just sanitize the raw content
      return notes;
    }
  }, [notes, hasHTML]);

  // Custom markdown components for consistent styling
  const markdownComponents: {
    [key: string]: React.ComponentType<any>;
  } = {
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="mb-2 last:mb-0">{children}</p>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <strong className="font-semibold text-foreground">{children}</strong>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
      <em className="italic">{children}</em>
    ),
    u: ({ children }: { children?: React.ReactNode }) => (
      <u className="underline">{children}</u>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="list-disc pl-4 mb-2">{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="list-decimal pl-4 mb-2">{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li className="mb-1">{children}</li>
    ),
    h1: ({ children }: { children?: React.ReactNode }) => (
      <h1 className="text-xl font-bold mb-3 text-foreground">{children}</h1>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <h2 className="text-lg font-bold mb-2 text-foreground">{children}</h2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <h3 className="text-base font-bold mb-2 text-foreground">{children}</h3>
    ),
    h4: ({ children }: { children?: React.ReactNode }) => (
      <h4 className="text-sm font-bold mb-2 text-foreground">{children}</h4>
    ),
    h5: ({ children }: { children?: React.ReactNode }) => (
      <h5 className="text-xs font-bold mb-2 text-foreground">{children}</h5>
    ),
    h6: ({ children }: { children?: React.ReactNode }) => (
      <h6 className="text-xs font-bold mb-1 text-foreground">{children}</h6>
    ),
    code: ({ children }: { children?: React.ReactNode }) => (
      <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono text-foreground">{children}</code>
    ),
    pre: ({ children }: { children?: React.ReactNode }) => (
      <pre className="bg-muted p-3 rounded-lg overflow-x-auto mb-2 text-xs font-mono">{children}</pre>
    ),
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <blockquote className="border-l-2 border-primary pl-3 italic text-muted-foreground">{children}</blockquote>
    ),
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <a href={href} className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">{children}</a>
    ),
    hr: () => <hr className="my-3 border-border" />,
    span: ({ children, className }: { children?: React.ReactNode; className?: string }) => (
      <span className={className}>{children}</span>
    ),
    div: ({ children, className }: { children?: React.ReactNode; className?: string }) => (
      <div className={className}>{children}</div>
    ),
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4">
      <div className="bg-card w-full max-w-2xl max-h-[85vh] rounded-2xl border border-border overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-background flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">{title || t('notes_detail.title')}</h2>
              <p className="text-sm text-muted-foreground">
                {t('notes_detail.subtitle')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted/50 rounded-full transition-colors"
            title={t('notes_detail.close')}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-6">
            <div className="bg-muted/50 rounded-lg p-4 min-h-[200px]">
              <div className="text-foreground prose prose-sm max-w-none break-words">
                {notes ? (
                  hasHTML ? (
                    // Render HTML directly (already sanitized)
                    <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
                  ) : (
                    // Render Markdown
                    <ReactMarkdown components={markdownComponents}>
                      {sanitizedContent}
                    </ReactMarkdown>
                  )
                ) : (
                  <em className="text-muted-foreground">{t('notes_detail.no_notes')}</em>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 p-6 border-t border-border bg-background">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            {t('notes_detail.close')}
          </button>
        </div>
      </div>
    </div>
  );
};
